/**
 * The lapsed-session gate. A sheet, never a bare error, opened when a follow
 * write answers UNAUTHENTICATED. `Sign in` sends the fan to `/sign-in` with
 * a redirect back to the exact team URL. Auto-completing the follow on
 * return is not built here; see ISSUE-182's plan `## 3.6` item 4.
 */
import { Button, Dialog } from "@sportsmet/ui";
import { useNavigate } from "@tanstack/react-router";
import type { ReactNode } from "react";

import {
  SIGN_IN_GATE_BODY,
  SIGN_IN_GATE_PRIMARY,
  SIGN_IN_GATE_SECONDARY,
  SIGN_IN_GATE_TITLE,
} from "./teamFollowLogic";

export const TEAM_SIGN_IN_SHEET = "team-sign-in";

export type typeTeamSignInGateDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export const TeamSignInGateDialog = (
  props: typeTeamSignInGateDialogProps,
): ReactNode => {
  // No `from` -- same reasoning as `GameDetailScreen.tsx` and `useOverlay.ts`:
  // this component renders under `/scores` today, but pinning `from` would
  // tie it to that one route id and break under the throwaway single-route
  // router `renderInShell` uses in tests.
  const navigate = useNavigate();

  const onSignIn = (): void => {
    void navigate({
      to: "/sign-in",
      search: {
        step: "email",
        redirect: window.location.pathname + window.location.search,
      },
    });
  };

  return (
    <Dialog
      open={props.open}
      onOpenChange={props.onOpenChange}
      title={SIGN_IN_GATE_TITLE}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => {
              props.onOpenChange(false);
            }}
          >
            {SIGN_IN_GATE_SECONDARY}
          </Button>
          <Button variant="primary" onClick={onSignIn}>
            {SIGN_IN_GATE_PRIMARY}
          </Button>
        </>
      }
    >
      <p className="text-body text-ink-secondary">{SIGN_IN_GATE_BODY}</p>
    </Dialog>
  );
};
